"use client";
import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import { useCart } from "./CartContext";

const PromoCodeCheckContext = createContext();

export { PromoCodeCheckContext };

export const PromoCodeCheckProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);

  const { cart } = useCart();

  const checkPromoCode = async (code, orderValue) => {
    setLoading(true);
    setError(null);

    try {
      // Get user_id from localStorage
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id;

      if (!userId) {
        setError("User not found. Please login again.");
        return {
          success: false,
          message: "User not found. Please login again.",
        };
      }

      console.log("Sending promo code request:", {
        code: code,
        user_id: userId,
        order_value: orderValue,
      });

      const response = await axios.post(
        "https://acsdev.in/manaprint_backend/public/api/client/promo-code/check",
        {
          code: code,
          user_id: userId,
          order_value: orderValue,
        },
      );

      console.log("Promo code API response:", response.data);

      if (response.data.status === "200") {
        const { product_id, category_id, value } = response.data.data;

        console.log("Promo code data:", { product_id, category_id, value });
        console.log("Cart items:", cart);
        console.log(
          "Cart item details:",
          cart.map((item) => ({
            id: item.id,
            categoryId: item.categoryId,
            name: item.name,
          })),
        );

        // Parse product_id and category_id (they can be comma-separated strings)
        const eligibleProductIds = product_id
          ? product_id
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id))
          : [];

        const eligibleCategoryIds = category_id
          ? category_id
              .split(",")
              .map((id) => parseInt(id.trim()))
              .filter((id) => !isNaN(id) && id !== 0)
          : [];

        console.log("Eligible product IDs:", eligibleProductIds);
        console.log("Eligible category IDs:", eligibleCategoryIds);

        // Check if any cart item matches the promo code criteria
        let isPromoValid = false;
        let matchedItems = [];

        // Check each cart item
        for (const cartItem of cart) {
          const itemId = parseInt(cartItem.id);
          const itemCategoryId = parseInt(cartItem.categoryId);

          console.log(
            `Checking cart item: ID=${itemId}, CategoryID=${itemCategoryId}, Name=${cartItem.name}`,
          );

          // Check if item matches by product ID
          if (
            eligibleProductIds.length > 0 &&
            eligibleProductIds.includes(itemId)
          ) {
            console.log(`✅ Product ID match found: ${itemId}`);
            isPromoValid = true;
            matchedItems.push({
              type: "product",
              id: itemId,
              name: cartItem.name,
            });
          }

          // Check if item matches by category ID
          if (
            eligibleCategoryIds.length > 0 &&
            !isNaN(itemCategoryId) &&
            eligibleCategoryIds.includes(itemCategoryId)
          ) {
            console.log(
              `✅ Category ID match found: ${itemCategoryId} for product ${itemId}`,
            );
            isPromoValid = true;
            matchedItems.push({
              type: "category",
              id: itemId,
              categoryId: itemCategoryId,
              name: cartItem.name,
            });
          }
        }

        console.log("Promo validation result:", { isPromoValid, matchedItems });

        if (isPromoValid) {
          // Success - at least one product or category matches
          const discountValue = parseFloat(value);
          setAppliedPromoCode({
            code: code,
            productIds: eligibleProductIds,
            categoryIds: eligibleCategoryIds,
            discount: discountValue,
            matchedItems: matchedItems,
          });
          setPromoDiscount(discountValue);

          console.log("Promo code applied successfully:", {
            code,
            discount: discountValue,
            matchedItems,
          });

          return {
            success: true,
            message: "Promo code applied successfully!",
            discount: discountValue,
            code: code,
            productIds: eligibleProductIds,
            categoryIds: eligibleCategoryIds,
            matchedItems: matchedItems,
          };
        } else {
          // No matching products or categories found
          console.log("No matching products or categories found in cart");
          console.log(
            "Available cart items:",
            cart.map((item) => ({
              id: item.id,
              categoryId: item.categoryId,
              name: item.name,
            })),
          );

          setError("This promo code is not applicable to items in your cart");
          return {
            success: false,
            message: "This promo code is not applicable to items in your cart",
          };
        }
      }
    } catch (err) {
      console.error("Promo code API error:", err);
      let errorMessage = "Failed to validate promo code";

      if (err.response?.data) {
        console.log("Error response data:", err.response.data);

        // Handle specific error messages from API
        if (err.response.data.message === "Minimum order value not met") {
          errorMessage = "Minimum order value not met for this promo code";
        } else if (
          err.response.data.message === "Promo code expired or not active"
        ) {
          errorMessage = "Promo code has expired or is not active";
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.status === "404") {
          errorMessage = "Invalid promo code";
        }
      }

      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromoCode(null);
    setPromoDiscount(0);
    setError(null);
  };

  const clearPromoState = () => {
    setAppliedPromoCode(null);
    setPromoDiscount(0);
    setError(null);
    setLoading(false);
  };

  return (
    <PromoCodeCheckContext.Provider
      value={{
        loading,
        error,
        appliedPromoCode,
        promoDiscount,
        checkPromoCode,
        removePromoCode,
        clearPromoState,
      }}
    >
      {children}
    </PromoCodeCheckContext.Provider>
  );
};

export const usePromoCodeCheck = () => {
  const context = useContext(PromoCodeCheckContext);
  if (!context) {
    throw new Error(
      "usePromoCodeCheck must be used within a PromoCodeCheckProvider",
    );
  }
  return context;
};
